import { useMemo, useState } from "react";

import Table from "examples/Tables/Table";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { FaEye, FaPen, FaTrashAlt, FaUser } from "react-icons/fa";
import dayjs from "dayjs";
import usePermissions from "hooks/usePermissions";

const { primary, dark, light } = colors;

const TaskCell = ({ title, roomArea }) => (
  <VuiBox>
    <VuiTypography variant="body2" fontWeight="regular" sx={{ fontSize: "14px" }}>
      {title}
    </VuiTypography>

    <VuiTypography variant="caption">{roomArea}</VuiTypography>
  </VuiBox>
);

const PriorityCell = ({ priority }) => {
  const colorMap = {
    Pending: "#f59e0b",
    Scheduled: "#3b82f6",
    InProgress: "#8b5cf6",
    "In Progress": "#8b5cf6",
    Completed: "#22c55e",
  };

  return (
    <VuiBadge
      variant="gradient"
      color={colorMap[priority] || "secondary"}
      size="sm"
      badgeContent={priority}
    />
  );
};

const AssignedCell = ({ name }) => (
  <VuiBox display="flex" alignItems="center" gap={1}>
    <FaUser size={10} />

    <VuiTypography variant="caption" fontWeight="regular">
      {name || "Unassigned"}
    </VuiTypography>
  </VuiBox>
);

const StatusCell = ({ status }) => {
  const colorMap = {
    Pending: "#f59e0b",
    Scheduled: "#3b82f6",
    "In Progress": "#3b82f6",
    Completed: "#22c55e",
  };

  const color = colorMap[status] || "#94a3b8";

  return (
    <VuiBox
      px={1.5}
      py={0.5}
      borderRadius="12px"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        background: `${color}15`,
        border: `1px solid ${color}50`,
      }}
    >
      <VuiTypography
        variant="caption"
        fontWeight="regular"
        sx={{
          color,
          fontSize: "10px",
        }}
      >
        {status || "—"}
      </VuiTypography>
    </VuiBox>
  );
};

const CostCell = ({ cost }) => (
  <VuiTypography variant="body2">${Number(cost || 0).toLocaleString()}</VuiTypography>
);

const ActionCell = ({ onView, onEdit, onDelete }) => {
  const { can } = usePermissions();

  return (
    <VuiBox display="flex" justifyContent="center" gap={1}>
      <VuiButton size="small" color="primary" variant="outlined" onClick={onView}>
        <FaEye size={11} />
      </VuiButton>

      {can("editMaintenance") && (
        <VuiButton size="small" color="primary" variant="outlined" onClick={onEdit}>
          <FaPen size={11} />
        </VuiButton>
      )}

      {can("deleteMaintenance") && (
        <VuiButton size="small" variant="outlined" color="error" onClick={onDelete}>
          <FaTrashAlt size={11} />
        </VuiButton>
      )}
    </VuiBox>
  );
};

function MaintenanceTable({
  tasks,
  page,
  rowsPerPage,
  total,
  loading,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
}) {
  const tableData = useMemo(
    () => ({
      columns: [
        { name: "task", align: "left" },
        { name: "category", align: "left" },
        // { name: "priority", align: "center" },
        { name: "status", align: "center" },
        { name: "scheduled", align: "center" },
        { name: "cost", align: "center" },
        { name: "assigned", align: "left" },
        { name: "actions", align: "center" },
      ],

      rows: tasks.map((task) => ({
        task: <TaskCell title={task.taskTitle} roomArea={task.roomArea} />,

        category: <VuiTypography variant="caption">{task.category}</VuiTypography>,

        status: <StatusCell status={task.status} />,

        scheduled: (
          <VuiTypography variant="caption">
            {task.scheduledDate ? dayjs(task.scheduledDate).format("MMM DD, YYYY") : "—"}
          </VuiTypography>
        ),

        cost: <CostCell cost={task.estimatedRepairCost} />,

        assigned: (
          <AssignedCell
            name={
              Array.isArray(task.assignedStaff) && task.assignedStaff.length > 0
                ? task.assignedStaff.map((staff) => staff.name).join(", ")
                : "Unassigned"
            }
          />
        ),

        actions: (
          <ActionCell
            onView={() => onView(task)}
            onEdit={() => onEdit(task)}
            onDelete={() => onDelete(task)}
          />
        ),
      })),
    }),
    [tasks]
  );

  return (
    <Table
      columns={tableData.columns}
      rows={tableData.rows}
      loading={loading}
      pagination
      paginationMode="server"
      page={page}
      rowsPerPage={rowsPerPage}
      total={total}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
}

export default MaintenanceTable;
